/**
 * Component-level overrides — density tuned for data-dense enterprise
 * screens, plus the accessibility baseline (STEP 14): every
 * interactive element gets a real, visible focus ring
 * (`:focus-visible`, not `:focus`, so a mouse click doesn't show a
 * ring a keyboard Tab press should) — MUI's own defaults are close
 * but inconsistent across components, this makes it uniform.
 */
import { alpha, type Components, type Theme } from '@mui/material/styles';

const focusRingStyle = {
  outline: '2px solid var(--mui-palette-primary-main, #C15F3C)',
  outlineOffset: 2,
};

/**
 * Enterprise UI Refinement — `StatusBadge` (the one shared status-chip
 * component used across every list page) renders `variant="filled"`
 * for any non-default status. MUI's stock filled Chip is a solid,
 * saturated fill with white text — calm for an occasional accent, but
 * fatiguing when a table has a whole column of them for an 8-12 hour
 * shift. Softening every semantic color's filled variant to a ~14%
 * tint (background) + the color's own darker shade (text) here — one
 * edit — recolors every `StatusBadge` app-wide with zero per-page
 * changes, since it's a theme-level `variants` rule keyed on
 * `ownerState`, not a `StatusBadge` prop change.
 */
const SOFT_FILLED_CHIP_COLORS = ['primary', 'secondary', 'success', 'warning', 'error', 'info'] as const;

/**
 * Enterprise UI Refinement — "Add depth & polish" pass. No new
 * duration/easing tokens invented: every hover/interaction transition
 * below reuses MUI's own `theme.transitions.create()` at
 * `duration.shorter` (200ms), landing inside the 150–250ms range real
 * enterprise UX guidance calls for, applied consistently instead of
 * the ad hoc per-file hardcoding (`WidgetShell.tsx`, `StatTile.tsx`)
 * that existed before this pass.
 */
const HOVER_TRANSITION_PROPS = ['background-color', 'box-shadow', 'border-color', 'transform'];

export const components: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: {
      '*:focus-visible': focusRingStyle,
    },
  },
  MuiAppBar: {
    // Chrome stays flat (matches the Sidebar's own flat dark treatment
    // and DESIGN-SYSTEM.md's "calm chrome" philosophy) — depth in this
    // pass goes to CONTENT (cards/dialogs/menus), not navigation. MUI's
    // default AppBar elevation is 4, which would otherwise contradict
    // that; no override existed here before this pass.
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: ({ theme }) => ({
        borderBottom: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 6,
        paddingBlock: 6,
        transition: theme.transitions.create(HOVER_TRANSITION_PROPS, { duration: theme.transitions.duration.shorter }),
      }),
    },
  },
  MuiTextField: {
    defaultProps: { size: 'small' },
  },
  MuiSelect: {
    defaultProps: { size: 'small' },
  },
  MuiTableCell: {
    styleOverrides: {
      root: { paddingBlock: 8, fontSize: '0.8125rem' },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 4,
        fontWeight: 500,
        transition: theme.transitions.create(HOVER_TRANSITION_PROPS, { duration: theme.transitions.duration.shorter }),
      }),
    },
    variants: SOFT_FILLED_CHIP_COLORS.map((color) => ({
      props: { variant: 'filled', color },
      style: ({ theme }) => ({
        backgroundColor: alpha(theme.palette[color].main, 0.14),
        color: theme.palette[color].dark ?? theme.palette[color].main,
      }),
    })),
  },
  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: 'none' },
    },
  },
  MuiCard: {
    styleOverrides: {
      // Radius bumped 8 -> 12 (cards read as the "polished" surface,
      // deliberately a step larger than the 6px buttons/list items
      // sitting inside them). Hover lift follows the exact pattern
      // `WidgetShell.tsx` already hand-rolled for dashboard widget
      // cards — same shape, now theme-driven so every Card gets it,
      // not just that one file.
      root: ({ theme }) => ({
        borderRadius: 12,
        transition: theme.transitions.create(['box-shadow', 'transform'], { duration: theme.transitions.duration.shorter }),
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-1px)',
        },
      }),
    },
  },
  MuiDialog: {
    styleOverrides: {
      /*
        A CLICK OUTSIDE A DIALOG NEVER CLOSES IT — application-wide.

        Desk, 2026-09-06: "whole work will get exhausted if user clicks by miss
        somewhere out of the dialog box." On this product a dialog routinely
        holds a form somebody has been typing into for minutes — the
        registration preview, an IP charge capture, a payment split — and MUI's
        default is to discard it on a stray click. That is the wrong default
        for a hospital counter, where a mis-click is not a rare event.

        WHY THIS IS CSS AND NOT 99 EDITED CALL SITES. There are 99 `<Dialog`
        usages across 90 files and exactly 2 guarded `reason === 'backdropClick'`.
        MUI v6 offers no global switch (`disableBackdropClick` was removed in
        v5), and `onClose` cannot be defaulted from the theme because an
        instance prop always wins. A style override cannot be forgotten by the
        next dialog somebody adds.

        HOW IT WORKS, precisely — from `@mui/material/Dialog/Dialog.js`:
          · `onMouseDown` on `.MuiDialog-container` records
            `backdropClick = (target === currentTarget)` — true only when the
            press landed on the container itself, i.e. outside the paper.
          · `onClick` on the Modal ROOT then closes, but ONLY if that ref is
            true.
        Taking the container out of hit-testing means the press lands on the
        BACKDROP beneath it instead, the ref is never set, and the root's
        handler returns early. No close.

        THE BACKGROUND STAYS INERT, which is the other half of the request. The
        backdrop still receives the click and still covers the viewport, so
        nothing behind the dialog is reachable — the click is absorbed, not
        passed through.

        ESCAPE AND THE DIALOG'S OWN CLOSE BUTTON ARE UNTOUCHED. Escape is a
        deliberate keypress, not a mis-click, and removing it would trap
        keyboard users (WCAG 2.1.2). Every dialog also keeps its explicit
        Cancel/close control.

        Safe here because nothing uses `scroll="body"` (verified: 0 call sites),
        which is the one mode that scrolls the container itself and would want
        pointer events on it.
      */
      container: { pointerEvents: 'none' },
      paper: { borderRadius: 12, pointerEvents: 'auto' },
    },
  },
  MuiTooltip: {
    defaultProps: { arrow: true },
  },
  MuiListItemButton: {
    styleOverrides: {
      // Only borderRadius/transition are new here — MUI's own
      // `.Mui-selected`/hover (`action.selected`/`action.hover`)
      // already applies by default and stays untouched for every
      // non-sidebar usage; the sidebar's own selected/hover state uses
      // the `sidebar.*` tokens directly in Sidebar.tsx instead, since
      // it needs different values against a dark field, not this
      // global rule.
      root: ({ theme }) => ({
        borderRadius: 6,
        transition: theme.transitions.create(['background-color'], { duration: theme.transitions.duration.shorter }),
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: { borderRadius: 12 },
    },
  },
};
